import { useCallback, useEffect, useRef, useState } from "react";
import { useTrialAccess } from "@/hooks/useTrialAccess";
import { isLapsedPathAllowed } from "@/lib/lapsedAccess";
import { useActiveZones } from "@/hooks/useNurseryConfig";
import {
  LayoutDashboard,
  Users,
  Baby,
  ArrowRightLeft,
  UserCog,
  CalendarDays,
  ClipboardList,
  PoundSterling,
  BarChart3,
  Settings,
  AlertTriangle,
  ShieldCheck,
  Shield,
  NotebookPen,
  MessageSquareWarning,
  FileCheck,
  Activity,
  ScrollText,
  CheckCircle2,
  HelpCircle,
  GraduationCap,
  BookOpen,
  CalendarCheck,
  CalendarPlus,

  Bus,
  School,
  CalendarRange,
  LogOut,
  Lock,
  Gift,
  Wrench,
  ChevronDown,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { NavLink } from "@/components/NavLink";
import { roleLabel as roleLabelFor, roleWithSummary } from "@/lib/roleLabels";
import { useAuth } from "@/contexts/AuthContext";
import { useAmIDsl } from "@/hooks/useDsl";
import { UserAvatar } from "@/components/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { usePendingApprovalCount } from "@/hooks/useApprovals";
import { usePlan, PROFESSIONAL_ONLY_ROUTES } from "@/hooks/usePlan";
import { useIsNurslyAdmin } from "@/hooks/useMigration";
import { useNavSettings, UNHIDEABLE_ITEMS } from "@/hooks/useNavSettings";
import { getTourSectionList } from "@/data/tourSteps";
import { useTourContext } from "@/components/Layout";
import { useOnboardingProgress } from "@/hooks/useOnboardingProgress";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ReferralModal } from "@/components/referral/ReferralModal";

const mainNav = [
  { title: "Dashboard", url: "/dashboard", icon: LayoutDashboard },
  { title: "Children", url: "/children", icon: Users },
  { title: "Staff", url: "/staff", icon: UserCog },
  { title: "Rota", url: "/rota", icon: CalendarDays },
  { title: "Register", url: "/register", icon: ClipboardList },
  // Everything from here down is Professional-only, kept as one contiguous block
  // so the free-versus-paid boundary is obvious at a glance.
  { title: "Transitions", url: "/transitions", icon: ArrowRightLeft },
  { title: "Attendance", url: "/attendance-staffing", icon: Activity },
  { title: "Planned Changes", url: "/planned-changes", icon: CalendarDays },
  { title: "Additional Days", url: "/additional-days", icon: CalendarPlus, editorPlus: true },

  { title: "Funding", url: "/funding", icon: PoundSterling },
  { title: "Reports", url: "/reports", icon: BarChart3 },
  { title: "Alerts", url: "/alerts", icon: AlertTriangle },
  { title: "Compliance", url: "/compliance", icon: FileCheck },
  { title: "Accidents & Incidents", url: "/safeguarding-log", icon: NotebookPen },
  { title: "Complaints", url: "/complaints-log", icon: MessageSquareWarning },
  { title: "Confidential Safeguarding", url: "/confidential-safeguarding", icon: Lock, adminOnly: true, dslAllowed: true },

  { title: "Ofsted Readiness", url: "/ofsted-readiness", icon: ShieldCheck, adminOnly: true },
];

const staffNav = [
  { title: "Room Register", url: "/room-register", icon: ClipboardList },
  { title: "My Rota", url: "/my-rota", icon: CalendarCheck },
];

const pickupNav = [
  { title: "Booking Form", url: "/pickups/bookings", icon: ClipboardList },
  { title: "Today's Pickups", url: "/pickups/today", icon: Bus },
  { title: "Monthly Overview", url: "/pickups/monthly", icon: CalendarRange },
  { title: "Schools", url: "/pickups/schools", icon: School },
];

const zoneNav = [
  { title: "Baby Zone", url: "/zone/baby", icon: Baby },
  { title: "Toddler Zone", url: "/zone/toddler", icon: Baby },
  { title: "Pre-School", url: "/zone/preschool", icon: Baby },
  { title: "Out of School", url: "/zone/out_of_school", icon: GraduationCap },
];

const adminNav = [
  { title: "Approvals", url: "/approvals", icon: CheckCircle2, adminOnly: true },
  { title: "Access", url: "/access", icon: ShieldCheck, adminOnly: true },
  { title: "Audit Log", url: "/audit-log", icon: ScrollText, adminOnly: true },
  { title: "Settings", url: "/settings", icon: Settings },
  { title: "Security & Trust", url: "/security", icon: Shield },
];

/* ─── Nursly wordmark for sidebar ─── */
import { NurslyLogo } from "@/components/NurslyLogo";
const SidebarLogo = () => <NurslyLogo size={22} tone="white" />;

/* ─── Nav group label ─── */
const NavGroupLabel = ({ children }: { children: string }) => (
  <SidebarGroupLabel
    className="text-[10px] font-semibold uppercase tracking-[0.1em] font-body"
    style={{ color: "rgba(255,255,255,0.35)" }}
  >
    {children}
  </SidebarGroupLabel>
);

/* ─── Styled nav link ─── */
const navBaseClass = "flex items-center gap-2 rounded-r-lg rounded-l-none px-3 py-2 text-sm font-body font-normal transition-colors";
const navInactiveStyle = "text-white/65 hover:bg-white/[0.06] hover:text-white/85";
const navActiveStyle = "bg-white/10 text-white font-medium border-l-[3px] border-nursly-sage";

export function AppSidebar() {
  const { profile, role, signOut } = useAuth();
  const isAdmin = role === "admin" || role === "super_admin";
  const isSuperAdmin = role === "super_admin";
  const { isDsl } = useAmIDsl();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const hoverTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const { data: pendingCount } = usePendingApprovalCount();
  const { isStarter } = usePlan();
  const [helpOpen, setHelpOpen] = useState(false);
  const [showTourPicker, setShowTourPicker] = useState(false);
  const [referralOpen, setReferralOpen] = useState(false);
  const { data: isNurslyAdmin } = useIsNurslyAdmin();
  const { data: navSettings } = useNavSettings();
  const { data: activeZones } = useActiveZones();
  const { isMobile, setOpenMobile } = useSidebar();
  // On mobile, tapping any nav item must immediately dismiss the sheet so the
  // user lands on the destination without an extra tap.
  const closeMobileNav = useCallback(() => {
    if (isMobile) setOpenMobile(false);
  }, [isMobile, setOpenMobile]);
  const hiddenItems = navSettings?.hidden_items || [];

  // Sidebar scroll affordance (B3-01)
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);
  const [pulseChevron, setPulseChevron] = useState(false);

  // One-time chevron pulse on first ever sidebar render
  useEffect(() => {
    try {
      const seen = localStorage.getItem("nursly:sidebar-chevron-seen");
      if (!seen) {
        setPulseChevron(true);
        localStorage.setItem("nursly:sidebar-chevron-seen", "1");
        const t = setTimeout(() => setPulseChevron(false), 4000);
        return () => clearTimeout(t);
      }
    } catch { /* ignore */ }
  }, []);

  // Restore scroll position per session
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    try {
      const saved = sessionStorage.getItem("nursly:sidebar-scroll");
      if (saved) el.scrollTop = parseInt(saved, 10) || 0;
    } catch { /* ignore */ }
    const update = () => {
      const more = el.scrollHeight - el.clientHeight - el.scrollTop > 8;
      setHasMoreBelow(more);
      try { sessionStorage.setItem("nursly:sidebar-scroll", String(el.scrollTop)); } catch { /* ignore */ }
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, []);

  const isItemHidden = (title: string) => {
    if (UNHIDEABLE_ITEMS.includes(title)) return false;
    return hiddenItems.includes(title);
  };

  let tourContext: { startTour: (fromStep?: string) => void } | null = null;
  try {
    tourContext = useTourContext();
  } catch {
    // Layout not mounted yet
  }

  const isLapsed = useTrialAccess().data?.access === "lapsed";
  const userPlan = (profile?.nursery_plan as "starter" | "professional" | undefined) ?? "professional";
  const tourSections = role ? getTourSectionList(role, userPlan) : [];

  const prefetchMap: Record<string, string> = {
    "/children": "children",
    "/staff": "staff",
    "/transitions": "transitions",
    "/rota": "shifts",
    "/dashboard": "dashboard",
  };

  const handleNavHover = useCallback((url: string) => {
    const key = prefetchMap[url];
    if (!key) return;
    hoverTimers.current[url] = setTimeout(() => {
      queryClient.prefetchQuery({
        queryKey: [key],
        staleTime: 60_000,
      });
    }, 300);
  }, [queryClient]);

  const handleNavLeave = useCallback((url: string) => {
    if (hoverTimers.current[url]) {
      clearTimeout(hoverTimers.current[url]);
      delete hoverTimers.current[url];
    }
  }, []);

  const roleDisplay = roleLabelFor(role);
  const roleHint = roleWithSummary(role);

  // B2-05: Pulse the Children / Staff / Rota icons until the corresponding
  // first-time activation action has been completed for this nursery.
  const onboarding = useOnboardingProgress();
  const pulseMap: Record<string, boolean> = {
    "/children": !onboarding.isLoading && !onboarding.hasAnyChild,
    "/staff": !onboarding.isLoading && !onboarding.hasAnyStaff,
    "/rota": !onboarding.isLoading && !onboarding.hasAnyShift,
  };

  const renderNavItem = (item: { title: string; url: string; icon: any; adminOnly?: boolean; superOnly?: boolean }, showBadge?: boolean) => {
    if (isLapsed && !isLapsedPathAllowed(item.url)) return null;
    const isActive = location.pathname === item.url || (item.url !== "/dashboard" && location.pathname.startsWith(item.url));
    const isLocked = isStarter && PROFESSIONAL_ONLY_ROUTES.some(r => item.url.startsWith(r));
    const shouldPulse = !!pulseMap[item.url];
    return (
      <SidebarMenuItem key={item.title}>
        <SidebarMenuButton asChild>
          <NavLink
            to={item.url}
            end={item.url === "/dashboard"}
            className={`${navBaseClass} ${isActive ? navActiveStyle : navInactiveStyle}`}
            activeClassName=""
            onClick={closeMobileNav}
            onMouseEnter={() => handleNavHover(item.url)}
            onMouseLeave={() => handleNavLeave(item.url)}
          >
            <span className={`relative inline-flex items-center justify-center ${shouldPulse ? "rounded-full animate-sage-pulse" : ""}`}>
              <item.icon className="h-4 w-4 shrink-0" style={{ color: shouldPulse ? "#52B788" : isActive ? "#52B788" : "rgba(255,255,255,0.5)" }} />
            </span>
            <span className="flex-1">{item.title}</span>
            {isLocked && (
              <Lock className="h-3 w-3 shrink-0" style={{ color: "rgba(255,255,255,0.3)" }} />
            )}
            {showBadge && pendingCount && pendingCount > 0 ? (
              <Badge variant="destructive" className="text-[9px] px-1.5 py-0 h-4 min-w-[1rem] flex items-center justify-center ml-auto">
                {pendingCount}
              </Badge>
            ) : null}
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };


  return (
    <Sidebar>
      <div className="relative flex min-h-0 flex-1 flex-col">
      <SidebarContent ref={scrollRef}>
        {/* Logo area */}
        <div className="px-5 pt-6 pb-4">
          <SidebarLogo />
        </div>

        <SidebarGroup>
          <NavGroupLabel>Main</NavGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNav.filter(item => (!(item as any).superOnly || role === "super_admin") && (!(item as any).adminOnly || isAdmin || ((item as any).dslAllowed && isDsl)) && (!(item as any).editorPlus || role !== "viewer") && !isItemHidden(item.title)).map(item => renderNavItem(item))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <div className="mx-4 my-1 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }} />

        {/* An ended (lapsed) nursery only keeps Children, Staff and Settings, so these sections are empty. */}
        {!isLapsed && (<>
        <SidebarGroup>
          <NavGroupLabel>Zones</NavGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {zoneNav.filter(item => {
                if (isItemHidden(item.title)) return false;
                // Filter by active zones if configured
                if (activeZones && activeZones.length > 0) {
                  const zoneId = item.url.replace("/zone/", "");
                  return activeZones.includes(zoneId);
                }
                return true;
              }).map(item => renderNavItem(item))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <div className="mx-4 my-1 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }} />

        <SidebarGroup>
          <NavGroupLabel>Staff</NavGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {staffNav.filter(item => !isItemHidden(item.title)).map(item => renderNavItem(item))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <div className="mx-4 my-1 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }} />

        <SidebarGroup>
          <NavGroupLabel>Pickups & Drop-offs</NavGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {pickupNav.filter(item => !isItemHidden(item.title)).map(item => renderNavItem(item))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <div className="mx-4 my-1 border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }} />
        </>)}

        <SidebarGroup>
          {!isLapsed && <NavGroupLabel>Admin</NavGroupLabel>}
          <SidebarGroupContent>
            <SidebarMenu>
              {adminNav
                .filter(item => {
                  if ((item as any).superOnly) return role === "super_admin";
                  if (item.adminOnly) return isAdmin;
                  return true;
                })
                .map(item => renderNavItem(item, item.title === "Approvals"))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Scroll affordance: gradient fade + chevron when more content is below (B3-01) */}
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-0 left-0 right-0 h-16 transition-opacity duration-300"
        style={{
          opacity: hasMoreBelow ? 1 : 0,
          background:
            "linear-gradient(to bottom, transparent 0%, hsl(var(--sidebar-background)) 85%)",
        }}
      />
      <div
        className="pointer-events-none absolute bottom-1 left-0 right-0 flex justify-center transition-opacity duration-300"
        style={{ opacity: hasMoreBelow ? 1 : 0 }}
        aria-hidden={!hasMoreBelow}
      >
        <ChevronDown
          size={12}
          className={pulseChevron ? "animate-bounce" : ""}
          style={{ color: "#52B788" }}
        />
      </div>
      </div>

      {/* Sidebar footer */}
      <SidebarFooter>
        <div className="border-t" style={{ borderColor: "rgba(255,255,255,0.08)" }} />

        {/* Nursly Admin, internal only */}
        {isNurslyAdmin && (
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild>
                <NavLink
                  to="/nursly-admin/migrations"
                  className={`${navBaseClass} ${location.pathname.startsWith("/nursly-admin") ? navActiveStyle : navInactiveStyle}`}
                  activeClassName=""
                  onClick={closeMobileNav}
                >
                  <Wrench className="h-4 w-4 shrink-0" style={{ color: "#B45309" }} />
                  <span style={{ color: "#B45309" }}>Nursly Admin</span>
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        )}

        {/* Refer a nursery, visible to all roles */}
        {(
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton asChild>
                <button
                  onClick={() => setReferralOpen(true)}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-body text-white/65 hover:bg-white/[0.06] hover:text-white/85 transition-colors cursor-pointer"
                >
                  <Gift className="h-4 w-4" style={{ color: "#52B788" }} />
                  <span>Refer a nursery</span>
                </button>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        )}

        {/* Help button */}
        <SidebarMenu>
          <SidebarMenuItem>
            <Popover open={helpOpen} onOpenChange={(open) => {
              setHelpOpen(open);
              if (!open) setShowTourPicker(false);
            }}>
              <PopoverTrigger asChild>
                <button
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-body text-white/65 hover:bg-white/[0.06] hover:text-white/85 transition-colors cursor-pointer"
                >
                  <HelpCircle className="h-4 w-4" style={{ color: "rgba(255,255,255,0.5)" }} />
                  <span>Help</span>
                </button>
              </PopoverTrigger>
              <PopoverContent side="top" align="start" className="w-64 p-2">
                {!showTourPicker ? (
                  <div className="space-y-1">
                    <button
                      onClick={() => setShowTourPicker(true)}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-body hover:bg-nursly-cream transition-colors text-left"
                    >
                      <GraduationCap className="h-4 w-4 text-nursly-muted" />
                      Take the platform tour
                    </button>
                    <button
                      onClick={() => {
                        setHelpOpen(false);
                        closeMobileNav();
                        navigate("/help");
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-body hover:bg-nursly-cream transition-colors text-left"
                    >
                      <BookOpen className="h-4 w-4 text-nursly-muted" />
                      Open Help Manual
                    </button>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs text-nursly-faint px-3 py-1.5 font-medium font-body">Which part would you like to revisit?</p>
                    <div className="space-y-0.5 max-h-60 overflow-y-auto">
                      <button
                        onClick={() => {
                          setHelpOpen(false);
                          setShowTourPicker(false);
                          tourContext?.startTour();
                        }}
                        className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-body hover:bg-nursly-cream transition-colors text-left font-medium"
                      >
                        🎉 Full tour from the start
                      </button>
                      {tourSections.map(s => (
                        <button
                          key={s.id}
                          onClick={() => {
                            setHelpOpen(false);
                            setShowTourPicker(false);
                            tourContext?.startTour(s.id);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-body hover:bg-nursly-cream transition-colors text-left"
                        >
                          {s.emoji} {s.label}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => setShowTourPicker(false)}
                      className="w-full text-xs text-nursly-faint px-3 py-1.5 mt-1 hover:text-nursly-ink font-body"
                    >
                      ← Back
                    </button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </SidebarMenuItem>
        </SidebarMenu>

        {/* User row */}
        <div className="flex items-center gap-2 px-4 py-3">
          <button
            onClick={() => { closeMobileNav(); navigate("/my-profile"); }}
            className="flex items-center gap-2 flex-1 min-w-0 hover:opacity-80 transition-opacity text-left"
          >
            <UserAvatar
              photoUrl={profile?.profile_photo_url || null}
              firstName={profile?.first_name || ""}
              lastName={profile?.last_name || ""}
              size={32}
              className="border-2 border-white/20"
            />
            <div className="flex-1 min-w-0">
              <span className="text-[13px] font-medium font-body block truncate" style={{ color: "rgba(255,255,255,0.75)" }}>
                {profile?.first_name || "Profile"}
              </span>
              {role && (
                <span
                  title={roleHint}
                  aria-label={roleHint}
                  className="text-[11px] font-body px-2 py-0 rounded-full inline-block"
                  style={{ background: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.6)" }}
                >
                  {roleDisplay}
                </span>
              )}
            </div>
          </button>
          <button
            onClick={() => signOut()}
            className="p-1.5 rounded-lg transition-colors hover:bg-white/[0.06]"
            title="Log out"
          >
            <LogOut className="h-4 w-4" style={{ color: "rgba(255,255,255,0.4)" }} />
          </button>
        </div>
      </SidebarFooter>

      {/* Referral modal */}
      {isSuperAdmin && (
        <ReferralModal open={referralOpen} onOpenChange={setReferralOpen} />
      )}
    </Sidebar>
  );
}
